import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import CardLayout from '../../../components/utils/CardLayout';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../components/utils/Button';
import Alert from '../../../components/utils/Alert';
import SearchBar from '../../../components/utils/SearchBar';
import { convertDateTimeToRequiredFormatTZ, isAdmin, isAdminAgent } from '../../../helpers/commonFunction';
import Tag from '../../../components/utils/Tag';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataTable from '../../../components/utils/DataTableClient';
import moment from 'moment';
import dealerAction from '../../../redux/dealer/actions';
import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import Calendar2LineIcon from 'remixicon-react/Calendar2LineIcon';
import Modal from '../../../components/utils/Model';
import EyeLineIcon from 'remixicon-react/EyeLineIcon';
import Download2LineIcon from 'remixicon-react/Download2LineIcon';
import BulkQrReportActions from '../../../redux/BulkQrReport/actions';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const { getBulkQrReport, resetSucessMessage, getBulkQrDetails } = BulkQrReportActions;
const { getDealer } = dealerAction;

const QR_CHUNK_SIZE = 50;
const PDF_MARGIN = 10;
const PDF_HEADER_H = 12;
const PDF_CARD_H = 50;
const PDF_QR_SIZE = 36;

const BulkQrReport = () => {
  const dispatch = useDispatch();
  const { bulkQrReportList, bulkQrReportLoading, searchButtonLoading, bulkQrDetailsList, bulkQrDetailsLoading, bulkQrDetailsError, } = useSelector((state) => state.bulkQRReport);
  const dealerResult = useSelector((state) => state.dealer.dealerResult);
  const { tokenData } = useSelector((state) => state.auth);
  const [filterText, setFilterText] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertText, setAlertText] = useState('');
  const [downloadingBatchID, setDownloadingBatchID] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [txnRow, setTxnRow] = useState(null);
  const [qrPreview, setQrPreview] = useState({});
  const [toDate, setToDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(new Date(new Date().getTime() - 2 * 24 * 60 * 60 * 1000));
  const [searchButtonData] = useState({
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    toDate: moment(toDate).format('YYYY-MM-DD'),
    dealerID: '',
    txnNumber: '',
  });

  const { register: registerFilter, handleSubmit: handleSearch } = useForm({ defaultValues: searchButtonData });
  const datePickerRef = useRef(null);
  const datePickerRef2 = useRef(null);
  const openDatePicker = () => datePickerRef.current.setOpen(true);
  const openDatePicker2 = () => datePickerRef2.current.setOpen(true);
  const showDealerFilter = isAdmin(tokenData) || isAdminAgent(tokenData);

  const cleanLpa = useCallback((value) => {
    const str = String(value || '');
    const start = str.toUpperCase().indexOf('LPA:');
    return (start >= 0 ? str.slice(start) : str).replace(/\s+/g, '').trim();
  }, []);

  const toSimCard = useCallback(
    (item) => ({
      id: item.TransactionID,
      transactionID: item.TransactionID,
      sim: item.SerialNumber,
      mobile: item.MSISDN,
      plan: item.PlanName,
      lpa: cleanLpa(item.LPA),
      zip: item.AreaCode,
    }),
    [cleanLpa]
  );

  const toTxnRow = useCallback(
    (item, index) => ({
      id: index + 1,
      txnid: item.TransactionID,
      serialNumber: item.SerialNumber,
      msisdn: item.MSISDN,
      requestStatus: item.RequestStatus,
      remarks: item.Remark,
    }),
    []
  );

  const detailsArray = Array.isArray(bulkQrDetailsList) ? bulkQrDetailsList : [];

  const viewCards = useMemo(
    () => (viewRow ? detailsArray.filter((item) => item.RequestStatusID === 12).map(toSimCard) : []),
    [viewRow, bulkQrDetailsList, toSimCard]
  );

  const pdfCards = useMemo(() => viewCards.filter((card) => card.lpa), [viewCards]);

  const txnRows = useMemo(() =>
    (txnRow ? detailsArray.map(toTxnRow) : []),
    [txnRow, bulkQrDetailsList, toTxnRow]
  );

  // ---------- Effects ----------
  useEffect(() => {
    dispatch(getBulkQrReport(searchButtonData));
    dispatch(getDealer());
    dispatch(resetSucessMessage());
  }, []);

  // View modal ke QR preview
  useEffect(() => {
    if (!viewRow) {
      setQrPreview({});
      return;
    }
    let cancelled = false;

    (async () => {
      const map = {};
      for (const card of viewCards) {
        if (!card.lpa) continue;
        try {
          map[card.id] = await QRCode.toDataURL(card.lpa, { width: 160, margin: 0 });
        } catch (error) {
          console.error('QR generation failed:', error);
        }
      }
      if (!cancelled) setQrPreview(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [viewRow, viewCards]);

  // ---------- Handlers ----------
  const displayAlert = (text, type) => {
    setAlertText(text);
    setAlertType(type);
    setShowAlert(true);
  };

  const alertCancel = () => {
    setShowAlert(false);
    setAlertText('');
    setAlertType('');
  };

  const handleViewBatch = (row) => {
    alertCancel();
    setQrPreview({});
    setViewRow(row);
    dispatch(getBulkQrDetails({ transactionID: row.bulktransactionid }));
  };

  const handleViewTxnDetails = (row) => {
    alertCancel();
    setTxnRow(row);
    dispatch(getBulkQrDetails({ transactionID: row.bulktransactionid }));
  };

  const handleSearchData = (formData) => {
    dispatch(
      getBulkQrReport({
        ...formData,
        toDate: moment(new Date(toDate)).format('YYYY-MM-DD'),
        fromDate: moment(new Date(fromDate)).format('YYYY-MM-DD'),
      })
    );
  };

  const handleDownloadQrPdf = async (row) => {
    if (!row?.bulktransactionid || downloadingBatchID) return;

    const cards = pdfCards;
    if (cards.length === 0) {
      displayAlert('No processed SIMs found in this batch.', 'danger');
      return;
    }

    alertCancel();
    setDownloadingBatchID(row.bulktransactionid);

    try {
      const qrImages = [];
      for (let i = 0; i < cards.length; i += QR_CHUNK_SIZE) {
        const chunk = cards.slice(i, i + QR_CHUNK_SIZE);
        const urls = await Promise.all(
          chunk.map((card) => QRCode.toDataURL(card.lpa, { width: 300, margin: 0 }))
        );
        qrImages.push(...urls);
      }

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const cardW = pageW - PDF_MARGIN * 2;
      const perPage = Math.floor((pageH - PDF_MARGIN * 2 - PDF_HEADER_H) / PDF_CARD_H);
      const textX = PDF_MARGIN + 8;
      const textW = cardW - PDF_QR_SIZE - 24;

      const drawHeading = () => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(0);
        pdf.text(`Batch TXNID: ${row.bulktransactionid}`, PDF_MARGIN, PDF_MARGIN + 6);
        pdf.setFont('helvetica', 'normal');
      };

      drawHeading();

      cards.forEach((card, i) => {
        const slot = i % perPage;
        if (i > 0 && slot === 0) {
          pdf.addPage();
          drawHeading();
        }

        const y = PDF_MARGIN + PDF_HEADER_H + slot * PDF_CARD_H;

        pdf.setDrawColor(200);
        pdf.setLineWidth(0.3);
        pdf.rect(PDF_MARGIN, y, cardW, PDF_CARD_H);

        pdf.setFontSize(10);
        pdf.setTextColor(0);
        const lines = [
          // `Transaction ID: ${card.transactionID}`,
          `SIM: ${card.sim}`,
          `Mobile: ${card.mobile}`,
          `Plan: ${card.plan}`,
          ...pdf.splitTextToSize(`LPAString: ${card.lpa}`, textW),
          `ZIP Code: ${card.zip}`,
        ];
        lines.forEach((line, li) => pdf.text(String(line), textX, y + 8 + li * 5.5));

        pdf.addImage(
          qrImages[i],
          'PNG',
          PDF_MARGIN + cardW - PDF_QR_SIZE - 8,
          y + (PDF_CARD_H - PDF_QR_SIZE) / 2,
          PDF_QR_SIZE,
          PDF_QR_SIZE
        );
      });

      pdf.save(`QR_${row.bulktransactionid}.pdf`);
      toast.success(`${cards.length} SIM cards downloaded in PDF`, { autoClose: 2000 });
    } catch (err) {
      console.error(err);
      displayAlert('Failed to generate the QR PDF file.', 'danger');
    } finally {
      setDownloadingBatchID(null);
    }
  };

  // ---------- Table columns ----------
  const columns = [
    { name: '#', selector: (row) => row.id, sortable: true, width: '80px', center: true },
    {
      name: 'REFERENCE',
      width: '300px',
      cell: (row) => {
        const isBulk = Number(row.IsBulk) === 1;
        return (
          <div className="flex flex-col py-2">
            <div>
              <strong>{isBulk ? 'Batch TXNID' : 'TXNID'}:</strong>{' '}
              <span
                onClick={() => handleViewTxnDetails(row)}
                style={{
                  cursor: 'pointer',
                  color: isBulk ? '#990404' : 'blue',
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
                title="View transactions"
              >
                {row.bulktransactionid || '-'}
              </span>
            </div>
            <div className="my-2">
              <strong>Dealer Name:</strong> {row.dealername || '-'}
            </div>
            <div>
              <strong>Dealer Email:</strong> {row.contactemailid || '-'}
            </div>
          </div>
        );
      },
    },
    { name: 'NETWORK', selector: (row) => row.network, width: '150px' },
    { name: 'ZIP CODE', selector: (row) => row.areacode, width: '120px' },
    {
      name: 'STATUS',
      cell: (row) => {
        switch (row.status) {
          case 'Completed':
            return <Tag color="badge-success" title="Completed" />;
          case 'Failed':
            return <Tag color="badge-danger" title="Failed" />;
          default:
            return <Tag color="badge-secondary" title="Partial" />;
        }
      },
    },
    { name: 'SUBMITTED DATE', selector: (row) => row.submitteddate, width: '180px' },
    {
      name: 'ACTION',
      cell: (row) => (
        <button
          type="button"
          onClick={() => handleViewBatch(row)}
          title="View"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <EyeLineIcon size="1.2em" style={{ color: '#880303' }} />
        </button>
      ),
    },
  ];

  const txnColumns = [
    { name: '#', selector: (row) => row.id, width: '70px', center: true },
    { name: 'TRANSACTION ID', selector: (row) => row.txnid || '-', width: '200px' },
    { name: 'SERIAL NUMBER', selector: (row) => row.serialNumber || '-', width: '210px' },
    { name: 'MSISDN', selector: (row) => row.msisdn || '-', width: '140px' },
    {
      name: 'REQUEST STATUS',
      width: '150px',
      cell: (row) => {
        const s = String(row.requestStatus || '').toLowerCase();
        if (!s) return '-';
        if (['completed', 'processed', 'success'].includes(s)) {
          return <Tag color="badge-success" title={row.requestStatus} />;
        }
        if (['failed', 'cancelled', 'error'].includes(s)) {
          return <Tag color="badge-danger" title={row.requestStatus} />;
        }
        return <Tag color="badge-secondary" title={row.requestStatus} />;
      },
    },
    { name: 'REMARKS', selector: (row) => row.remarks || '-', wrap: true },
  ];

  // ---------- Table data ----------
  const filteredItems = Array.isArray(bulkQrReportList) ? bulkQrReportList.filter((item) => item && typeof item === 'object' &&
    Object.values(item).some((value) => typeof value === 'string' && value.toLowerCase().includes(filterText.toLowerCase()))) : [];

  const data = filteredItems.map((item, index) => ({
    id: index + 1,
    bulktransactionid: item.BulkTransactionID || item.TransactionID,
    IsBulk: item.IsBulk,
    dealername: item.DealerName,
    contactemailid: item.ContactEmailID,
    network: item.NetworkName,
    areacode: item.AreaCode,
    submitteddate: item.RequestedDtTm ? convertDateTimeToRequiredFormatTZ(item.RequestedDtTm) : '',
    status: item.Status,
  }));

  return (
    <CardLayout title="Bulk QR Report">
      <ToastContainer />
      {showAlert && (
        <div className="mt-4">
          <Alert type={alertType} text={alertText} closeBtn={alertCancel} />
        </div>
      )}

      <div>
        <form onSubmit={handleSearch(handleSearchData)}>
          <div className="form-row">
            {showDealerFilter && (
              <div className="col-lg-2 col-md-4 mb-2">
                <label>Select Dealer</label>
                <select className="form-control" id="dealerID" name="dealerID" {...registerFilter('dealerID')}>
                  <option value="">All</option>
                  {dealerResult?.length > 0 &&
                    dealerResult.map((item) => (
                      <option value={item.ID} key={`dealer${item.ID}`}>
                        {item.DealerName}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div className="col-lg-2 col-md-4 mb-2 d-flex flex-column">
              <label>From Date</label>
              <Calendar2LineIcon
                onClick={openDatePicker}
                size={'1.5rem'}
                style={{ cursor: 'pointer', position: 'absolute', top: '36px', right: '12px', zIndex: 1 }}
              />
              <DatePicker
                ref={datePickerRef}
                selected={fromDate}
                className="form-control"
                onChange={(date) => setFromDate(date)}
                dateFormat="yyyy-MM-dd"
                name="fromDate"
                showMonthDropdown
                showYearDropdown
                maxDate={toDate}
                onKeyDown={(e) => e.preventDefault()}
              />
            </div>

            <div className="col-lg-2 col-md-4 mb-2 d-flex flex-column">
              <label>To Date</label>
              <Calendar2LineIcon
                onClick={openDatePicker2}
                size={'1.5rem'}
                style={{ cursor: 'pointer', position: 'absolute', top: '36px', right: '12px', zIndex: 1 }}
              />
              <DatePicker
                ref={datePickerRef2}
                selected={toDate}
                className="form-control center"
                onChange={(date) => setToDate(date)}
                dateFormat="yyyy-MM-dd"
                name="todate"
                showMonthDropdown
                showYearDropdown
                minDate={fromDate}
                onKeyDown={(e) => e.preventDefault()}
              />
            </div>

            <div className="col-lg-3 col-md-4 mb-2">
              <label>Batch/SIM/Mobile/TXN</label>
              <input
                type="text"
                className="form-control"
                id="txnNumber"
                name="txnNumber"
                placeholder="Batch/SIM/Mobile/TXN"
                {...registerFilter('txnNumber')}
              />
            </div>

            <div className={showDealerFilter ? 'col-lg-2 col-md-4 mb-2' : 'col-lg-2 col-md-4'}>
              <div className={showDealerFilter ? 'mt-2 d-flex justify-content-start activation-search-btn' : 'mt-4'}>
                <Button
                  title="Search"
                  style="minWidth:80px"
                  loading={searchButtonLoading}
                  buttonClass="btn btn-primary mt-1 btn-md Activity_searchBtn__GlBtC"
                  buttonType="submit"
                />
              </div>
            </div>
          </div>
        </form>
        <hr />

        <div className="row mb-3">
          <div className="col-lg-3 activation-request-search-btn mb-sm-444">
            <SearchBar onFilter={setFilterText} filterText={filterText} />
          </div>
        </div>

        <DataTable columns={columns} data={data} loading={bulkQrReportLoading} customStyles={tableStyles} />
      </div>

      {/* View modal: SIM cards + PDF download (sirf Processed) */}
      <Modal
        showModal={!!viewRow}
        title={`QR TRANSACTION Details: ${viewRow?.bulktransactionid || ''}`}
        onHide={() => setViewRow(null)}
        width={800}
        showFooter={false}
      >
        {bulkQrDetailsLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" role="status" />
          </div>
        ) : bulkQrDetailsError ? (
          <p style={modalStyles.text}>{String(bulkQrDetailsError)}</p>
        ) : (
          viewCards.length > 0 && (
            <>
              <div style={modalStyles.header}>
                <div style={modalStyles.subHeading}>Total Processed: {viewCards.length}</div>
                <button
                  type="button"
                  className="btn btn-primary"
                  title="Download PDF"
                  disabled={!!downloadingBatchID || pdfCards.length === 0}
                  onClick={() => handleDownloadQrPdf(viewRow)}
                  style={{ borderRadius: '5px', width: '75px', marginRight: '5px' }}
                >
                  {downloadingBatchID === viewRow?.bulktransactionid ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <Download2LineIcon size={25} />
                  )}
                </button>
              </div>

              <div style={modalStyles.list}>
                {viewCards.map((card) => (
                  <div key={card.id} style={modalStyles.card}>
                    <div>
                      {/* <div style={modalStyles.text}><b>Transaction ID:</b> {card.transactionID || '-'}</div> */}
                      <div style={modalStyles.text}><b>SIM:</b> {card.sim || '-'}</div>
                      <div style={modalStyles.text}><b>Mobile:</b> {card.mobile || '-'}</div>
                      <div style={modalStyles.text}><b>Plan:</b> {card.plan || '-'}</div>
                      <div style={modalStyles.text}><b>LPAString:</b> {card.lpa || '-'}</div>
                      <div style={modalStyles.text}><b>ZIP Code:</b> {card.zip || '-'}</div>
                    </div>

                    {card.lpa ? (
                      qrPreview[card.id] ? (
                        <img src={qrPreview[card.id]} alt={`QR ${card.transactionID}`} style={modalStyles.qr} />
                      ) : (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      )
                    ) : (
                      <span>No QR</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )
        )}
      </Modal>

      {/* Transactions modal: TXNID click */}
      <Modal
        showModal={!!txnRow}
        title={`TRANSACTION Details: ${txnRow?.bulktransactionid || ''}`}
        onHide={() => setTxnRow(null)}
        width={950}
        showFooter={false}
      >
        {bulkQrDetailsError ? (
          <p style={modalStyles.text}>{String(bulkQrDetailsError)}</p>
        ) : (
          <DataTable columns={txnColumns} data={txnRows} loading={bulkQrDetailsLoading} customStyles={tableStyles} />
        )}
      </Modal>
    </CardLayout>
  );
};
export default BulkQrReport;

const modalStyles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  subHeading: { fontSize: 14, color: '#555' },
  list: { maxHeight: '60vh', overflowY: 'auto' },
  card: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #ddd',
    padding: 12,
    marginBottom: 8,
  },
  text: { fontSize: 14, color: '#000', marginBottom: 2, wordBreak: 'break-all' },
  qr: { width: 100, height: 100 },
};

const tableStyles = {
  headRow: { style: { backgroundColor: '#FFF5F5' } },
  headCells: { style: { fontWeight: 'bold', fontSize: '14px' } },
};